/**
 * The chart, while it is still on its way. The web half.
 *
 * The counterpart to `apps/mobile/src/components/chart/ChartLoading.tsx`, and
 * deliberately a separate file rather than a shared component: the two render
 * with different primitives, and the part worth sharing — when to appear, what
 * to say — is already shared, in `@aegis/api-client`'s `loading.ts`. A
 * component abstract enough to draw a ring in both React DOM and Skia would be
 * a worse thing than two short files agreeing on three exported functions.
 *
 * What the phone found, which is why this exists at all: the web had a BOOT
 * screen and no chart-level loader, so switching symbols on a slow connection
 * gave a blank rectangle with nothing to say whether it was fetching, idle or
 * broken. Exactly the state the phone was in.
 *
 * The motion is CSS, and `reset.web.css` already honours
 * `prefers-reduced-motion` for the whole document; `app.css` names this ring
 * explicitly as well, because a spinner is the one thing a reader with that
 * setting on is most likely to have turned it on because of.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import {
  loadingLabel, loadStage, SHOW_AFTER_MS, SLOW_AFTER_MS, STALLED_AFTER_MS,
  type LoadStage,
} from '@aegis/api-client';

export interface ChartLoadingProps {
  /** The instrument being fetched, or null before one has been chosen. */
  symbol: string | null;
  /** Offered once the load has stalled. Omitted where there is nothing to retry. */
  onRetry?: () => void;
}

export function ChartLoading({ symbol, onRetry }: ChartLoadingProps): JSX.Element | null {
  /**
   * How much to say.
   *
   * Three fixed moments, so three timeouts rather than a ticking clock. The
   * thresholds are imported rather than written here, so what this schedules
   * and what `loadStage` reports — and what the phone does — cannot drift.
   */
  const [stage, setStage] = useState<LoadStage>(() => loadStage(0));
  useEffect(() => {
    const timers = [
      window.setTimeout(() => setStage('loading'), SHOW_AFTER_MS),
      window.setTimeout(() => setStage('slow'), SLOW_AFTER_MS),
      window.setTimeout(() => setStage('stalled'), STALLED_AFTER_MS),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [symbol]);

  // Nothing at all for the first fraction of a second, so a fast load never
  // flashes an indicator on its way past.
  if (stage === 'hidden') return null;

  return (
    <div className="chart-loading" role="status" aria-live="polite">
      <div className="chart-loading-mark" aria-hidden="true">
        <span className="chart-loading-ring" />
        <span className="chart-loading-ae">Æ</span>
      </div>
      <span className="chart-loading-label mono">{loadingLabel(symbol)}</span>
      {(stage === 'slow' || stage === 'stalled') && (
        <span className="chart-loading-slow">Taking longer than usual</span>
      )}
      {stage === 'stalled' && onRetry && (
        <button type="button" className="chart-loading-retry" onClick={onRetry}>Retry</button>
      )}
    </div>
  );
}
